import { getConfig } from '@local/app-config';
import { trackError } from '@local/metrics';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { skipToken } from '@reduxjs/toolkit/query';
import { useFlags } from 'launchdarkly-react-client-sdk';
import type { JSX } from 'react';
import { useContext, useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router';
import { v4 as uuid } from 'uuid';

import { useLoadBentleyLicenseOrgsQuery } from '../../apiClients/entitlementsClient/requests/BentleyEntitlementsAPI';
import {
    useLazyRefreshBentleyAccessTokenQuery,
    useLazyRetrieveBentleyAccessTokenQuery,
} from '../../apiClients/identityClient/requests/BentleyAccessTokenApi';
import {
    BENTLEY_SESSION_EXPIRED,
    EVO_APPS,
    EVO_NEXT_SERVICES,
    LOGIN_ERROR_ROUTE,
    LOGIN_ROUTE,
    LOGIN_SILENT,
    LOGOUT_ROUTE,
    LOGOUT_SUCCESS_ROUTE,
    type TokenScope,
} from '../../constants';
import {
    clearCombinedToken as clearAccessToken,
    getCombinedToken as getAccessToken,
} from '../../store/sessionStorageHelpers/accessTokenHelper/accessTokenHelper';
import { clearCSRFState, getCSRFState } from '../../store/sessionStorageHelpers/authStateHelpers';
import { setDeeplink } from '../../store/sessionStorageHelpers/deeplinkHelper/deeplinkHelper';
import { clearEntitlements } from '../../store/sessionStorageHelpers/entitlementsHelper/entitlementsHelper';
import { clearPkceVerifier } from '../../store/sessionStorageHelpers/pkceFlowHelpers';
import {
    LOGIN_SIGNIN,
    LOGOUT,
    LOGIN_RETRY,
    SESSION_HAS_EXPIRED_TEXT,
    SESSION_HAS_EXPIRED_TITLE,
    SESSION_HAS_EXPIRED_RETRY_TEXT,
} from '../../strings';
import { getBentleyAuthUrl } from '../../util/getAuthUrl';
import type { LoginHandlerContextState } from '../loginHandler/LoginContext';
import { LoginContext } from '../loginHandler/LoginContext';

function accessTokenInvalid(error: FetchBaseQueryError | SerializedError | undefined) {
    if (error) {
        // It is possible that if the user refreshes the page fast enough, that the original
        // request will be cancelled and RTK Query will return the
        // vague error: { status: 'FETCH_ERROR', error: 'TypeError: Failed to fetch' }
        // In this case, the access token is not invalid, and we do not want to redirect the user
        // back to the login page.
        const { status } = error as any;
        if (status && typeof status === 'number' && status >= 400) {
            return true;
        }
    }
    return false;
}

function accessTokenUnauthorized(error: FetchBaseQueryError | SerializedError | undefined) {
    if (error) {
        const { status } = error as any;
        if (status && typeof status === 'number' && status === 401) {
            return true;
        }
    }
    return false;
}

const HOUR_IN_MILLI = 3_600_000;
const SILENT_AUTH_POLL_TIME = 60000 * 25; // 25 mins to account for 2 attempts in a 1 hour window

function WrappedIframe({
    additionalScopes = [],
    tabId,
}: Readonly<{
    additionalScopes?: TokenScope[];
    tabId: string;
}>) {
    const { forceOauthPrefix } = useContext<LoginHandlerContextState>(LoginContext);
    const [silentAuthUrl, setSilentAuthUrl] = useState<URL | undefined>();
    const bentleyIdentityBaseUrl = getConfig().bentleyBaseUrl;
    // Contents of key aren't important. Changing the key allows us to nudge the iframe if it gets stuck
    const [iframeKey, setIframeKey] = useState(uuid());

    useEffect(() => {
        async function getSilentAuthUrl() {
            const url = await getBentleyAuthUrl({
                forceOauthPrefix,
                additionalScopes,
                tabId,
                silent: true,
            });

            setSilentAuthUrl(url);
        }
        if (!silentAuthUrl) {
            getSilentAuthUrl();
        }

        // Sometimes the iframe gets stuck if the Bentley page runs into an error,
        // this forces a reload by simply changing the key to any value if the iframe has taken longer than 2 minute to auth
        const timeoutId = setInterval(() => {
            setIframeKey(uuid());
        }, 60_000 * 2);

        return () => {
            clearInterval(timeoutId);
        };
    }, []);

    if (!bentleyIdentityBaseUrl || !silentAuthUrl) {
        return null;
    }

    return (
        <iframe
            automation-id="bentley-silent-auth-iframe"
            aria-hidden="true"
            id="BentleyAuthorize"
            title="BentleyAuthorize"
            style={{
                position: 'absolute',
                opacity: '0',
                width: '1px',
                height: '1px',
                top: '0',
                left: '0',
                border: 'none',
                display: 'block',
                zIndex: '-1',
                pointerEvents: 'none',
                visibility: 'hidden',
            }}
            height={0}
            width={0}
            tabIndex={-1}
            src={silentAuthUrl.toString()}
            key={iframeKey}
        />
    );
}

function isAccessTokenExpired() {
    const token = getAccessToken();
    const expiry = token?.expires_at;
    return expiry ? expiry <= Date.now() : true; // Missing attribute is considered expired
}

export function BentleyHiddenIframe({
    children,
    additionalScopes = [],
    dialogEnabled = true,
}: Readonly<{
    children: JSX.Element;
    additionalScopes?: TokenScope[];
    dialogEnabled?: boolean;
}>) {
    const navigate = useNavigate();
    const { forceOauthPrefix } = useContext(LoginContext);
    const [loadIframe, setLoadIframe] = useState(false);
    const [expiredToken, setExpiredToken] = useState(false);
    // Set an initial identifier to access local storage elements
    const [tabIdentifier] = useState(uuid());
    const [open, setOpen] = useState(false);
    const [hasClickedSignIn, setHasClickedSignIn] = useState(false);
    const handleOpen = () => {
        setHasClickedSignIn(false);
        setOpen(true);
    };
    const handleClose = () => {
        setOpen(false);
    };
    const [getNewAccessToken, getNewAccessTokenResult] = useLazyRetrieveBentleyAccessTokenQuery();
    const token = getAccessToken();
    const expiry = token?.expires_at;
    const expired = expiry ? expiry <= Date.now() : true; // Missing attribute is considered expired

    function iframeEventHandler(event: MessageEvent) {
        if (event.origin !== window.origin) {
            // If this is a message that didn't come specifically from us, ignore it.
            return;
        }

        if (event.data.source === 'bentley-silent-auth') {
            // Take iframe out of the DOM after it's done it's job.
            setLoadIframe(false);
            if (event.data.error) {
                // Ignore the error as long as we still have an access token, this just means our session is on the way out
                if (isAccessTokenExpired()) {
                    // Needed so we don't override the deeplink to the login/logout page after attempting to navigate there
                    if (!(
                        location.pathname.startsWith(LOGIN_ROUTE) ||
                        location.pathname.startsWith(LOGOUT_ROUTE)
                    )) {
                        const deepLink = `${location.pathname}${location.search}`;
                        setDeeplink(deepLink);
                    }
                    navigate(`${LOGIN_ROUTE}?error=${BENTLEY_SESSION_EXPIRED}`);
                } else {
                    handleOpen();
                }
            } else {
                // Check the CSRF state before we get a new access token
                // If the states don't match, bail out
                if (event.data.state !== getCSRFState(tabIdentifier)) {
                    // Needed so we don't override the deeplink to the login/logout page after attempting to navigate there
                    if (!(
                        location.pathname.startsWith(LOGIN_ROUTE) ||
                        location.pathname.startsWith(LOGOUT_ROUTE)
                    )) {
                        const deepLink = `${location.pathname}${location.search}`;
                        setDeeplink(deepLink);
                    }
                    trackError(
                        'Error CSRF states do not match',
                        'CSRF states do not match after silent auth attempt, potential CSRF attack',
                        {
                            request: getCSRFState(tabIdentifier).substring(0, 7),
                            response: event.data.state.substring(0, 7),
                            tabIdentifier: tabIdentifier.substring(0, 7),
                        },
                    );
                    navigate(`${LOGIN_ERROR_ROUTE}?error=invalid_auth_state&bentley_token=true`);
                    return;
                }
                getNewAccessToken({
                    authCode: event.data.code,
                    forceOauthPrefix,
                    tabId: tabIdentifier,
                });
            }
        }
    }

    useEffect(() => {
        // if a user navigates back to the page and 'resets' our attempt at silent auth
        // before the 25 minute mark we should check if, on init of component, our access token is 25 mins old
        // Try one silent-auth attempt straight away.
        if (expired) {
            // If we're already expired out the gates, stop rendering children and focus on getting that token
            setLoadIframe(true); // Activate the iframe to silent-auth
            setExpiredToken(true); // Stop checking for expiry as we're currently handling it
        } else if (expiry) {
            const lifeInMilli = expiry - Date.now(); // Life in Milliseconds
            const firstRefresh = HOUR_IN_MILLI - SILENT_AUTH_POLL_TIME;
            if (lifeInMilli < firstRefresh) {
                setLoadIframe(true);
            }
        }

        // Set up the initial loop of refreshing the session every 25 mins
        const timeoutId = setInterval(() => {
            setLoadIframe(true);
        }, SILENT_AUTH_POLL_TIME);

        window.addEventListener('message', iframeEventHandler);

        return () => {
            window.removeEventListener('message', iframeEventHandler);
            clearInterval(timeoutId);
            // Clear away the CSRF and PKCE verifier, it's tied to the tab identifier so once the component unmounts
            // clean them away so they don't clutter up local storage.
            clearCSRFState(tabIdentifier);
            clearPkceVerifier(tabIdentifier);
        };
    }, []);

    useEffect(() => {
        // This is an aggresive check to verify our access token isn't expired.
        // This accounts for a user putting their machine to sleep, waking it up and then trying to interact with the app.
        const expiredTimeoutId = setInterval(() => {
            if (!expiredToken) {
                if (isAccessTokenExpired()) {
                    setLoadIframe(true); // Activate the iframe to silent-auth
                    setExpiredToken(true); // Stop checking for expiry as we're currently handling it
                }
            }
        }, 1000);

        return () => {
            clearInterval(expiredTimeoutId);
        };
    }, [expiredToken]);

    useEffect(() => {
        // If error, add tracelog
        if (getNewAccessTokenResult.isError) {
            trackError(
                'Error on silent auth',
                'Could not get token from silent auth',
                getNewAccessTokenResult.error,
            );
        }
        // If success, recover from expired state if we were expired beforehand.
        if (getNewAccessTokenResult.data) {
            setExpiredToken(false);
            handleClose();
        }
    }, [getNewAccessTokenResult]);

    function makeBroadcastChannel() {
        // Make channel, specific to the CSRF state to ensure our exact request worked.
        const state = getCSRFState();
        const bc = new BroadcastChannel(`extend_login.${state}`);

        // Make function to listen for login extension
        bc.onmessage = (event) => {
            // Considering the channel includes the state I doubt I need to be this specific
            // but it doesn't hurt
            if (event.data.source === 'bentley-login-extension') {
                if (event.data.error) {
                    // Return where you were after getting kicked out
                    const deepLink = `${location.pathname}${location.search}`;
                    // Needed so we don't override the deeplink to the login/logout page after attempting to navigate there
                    if (!(
                        location.pathname.startsWith(LOGIN_ROUTE) ||
                        location.pathname.startsWith(LOGOUT_ROUTE)
                    )) {
                        setDeeplink(deepLink);
                    }

                    // If our login extension came up with an error, nothing for it than diverting to the login page
                    navigate(
                        `${LOGIN_ERROR_ROUTE}?error=error_extending_session&bentley_token=true`,
                    );
                } else {
                    // We have a new session, because we started it with IMS in the other tab
                    // Now we'll get a new access token in the background, in theory our old one should still have some life in it
                    getNewAccessToken({
                        authCode: event.data.code,
                        forceOauthPrefix,
                        extendSession: true,
                    });

                    bc.close();
                }
            }
        };
    }

    const signInCallback = async () => {
        setHasClickedSignIn(true);

        // Need to make the url first as that will generate a CSRF state
        const url = await getBentleyAuthUrl({
            forceOauthPrefix,
            additionalScopes,
            extendSession: true,
        });

        makeBroadcastChannel();

        // Define the size and location of the popup so it appears central to the parent window
        const width = 600;
        const height = 600;
        const y = window.outerHeight / 2 + window.screenY - height / 2;
        const x = window.outerWidth / 2 + window.screenX - width / 2;

        // Open the popup
        const newWindow = window.open(
            url,
            '_blank',
            `noopener,noreferrer,popup,width=${width},height=${height},left=${x},top=${y}`,
        );
        if (newWindow) newWindow.opener = null;
    };

    const childrenAndDialog = () => {
        if (dialogEnabled) {
            if (expiredToken || expired) {
                return (
                    <Stack
                        sx={{ height: '100vh', width: '100vw' }}
                        justifyContent="center"
                        alignItems="center"
                    >
                        <CircularProgress automation-id="access-token-renewal-progress" />
                    </Stack>
                );
            }
            return (
                <>
                    <Dialog
                        fullWidth
                        maxWidth="xs"
                        open={open}
                        automation-id="silent-auth-session-expired-dialog"
                    >
                        <DialogTitle>{SESSION_HAS_EXPIRED_TITLE}</DialogTitle>
                        {hasClickedSignIn ? (
                            <>
                                <DialogContent
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Stack direction="column">
                                        <DialogContentText>
                                            {SESSION_HAS_EXPIRED_RETRY_TEXT}
                                        </DialogContentText>
                                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                            <CircularProgress automation-id="expired-session-renewal-progress" />
                                        </Box>
                                    </Stack>
                                </DialogContent>
                                <DialogActions>
                                    <Button
                                        variant="outlined"
                                        color="secondary"
                                        onClick={() => {
                                            handleClose();
                                            navigate(`${LOGOUT_ROUTE}`);
                                        }}
                                    >
                                        {LOGOUT}
                                    </Button>
                                    <Button variant="contained" onClick={signInCallback}>
                                        {LOGIN_RETRY}
                                    </Button>
                                </DialogActions>
                            </>
                        ) : (
                            <>
                                <DialogContent>
                                    <DialogContentText>
                                        {SESSION_HAS_EXPIRED_TEXT}
                                    </DialogContentText>
                                </DialogContent>
                                <DialogActions>
                                    <Button
                                        variant="outlined"
                                        color="secondary"
                                        onClick={() => {
                                            handleClose();
                                            navigate(`${LOGOUT_ROUTE}`);
                                        }}
                                    >
                                        {LOGOUT}
                                    </Button>
                                    <Button variant="contained" onClick={signInCallback}>
                                        {LOGIN_SIGNIN}
                                    </Button>
                                </DialogActions>
                            </>
                        )}
                    </Dialog>
                    {children}
                </>
            );
        }
        return children;
    };

    return (
        <>
            {loadIframe && (
                <WrappedIframe additionalScopes={additionalScopes} tabId={tabIdentifier} />
            )}
            {childrenAndDialog()}
        </>
    );
}

function clearSession() {
    clearAccessToken();
    clearCSRFState();
    clearEntitlements();
    clearPkceVerifier();
}

export function BentleyAccessTokenGuard({
    children,
    additionalScopes = [],
}: Readonly<{
    children: JSX.Element;
    additionalScopes?: TokenScope[];
}>) {
    const location = useLocation();
    const navigate = useNavigate();
    const flags = useFlags();
    const combineResponses = flags.evouiCombineDevIntgInstanc ?? false;
    const [searchParams] = useSearchParams();
    const { forceOauthPrefix, service } = useContext(LoginContext);
    const [refreshToken, refreshTokenResult] = useLazyRefreshBentleyAccessTokenQuery();

    const authCode = searchParams.get('code');
    const errorCode = searchParams.get('error');

    const token = getAccessToken();
    const accessToken = token?.access_token;
    const expiry = token?.expires_at;
    const expired = expiry ? expiry <= Date.now() : true; // Missing attribute is considered expired

    const skipDiscovery =
        !accessToken ||
        (expired && !token?.refresh_token) ||
        service === 'none' ||
        service === 'central' ||
        flags.evouiCombineDevIntgInstanc === undefined; // Wait for launchdarkly flag to load to avoid duplicate requests
    // Check that our access or refresh token still works. We may have been logged out of another window, so
    // our auth may no longer apply.

    const { error } = useLoadBentleyLicenseOrgsQuery(
        skipDiscovery
            ? skipToken
            : {
                  service: service === 'evo-next' ? EVO_NEXT_SERVICES : service,
                  ...(combineResponses && EVO_APPS.includes(service) && { combineResponses }),
              },
    );
    const noAccessToken = !accessToken && !authCode;

    useEffect(() => {
        // Attempt to refresh the accessToken since the LicenseToken refresh failed
        if (accessTokenUnauthorized(error) && token?.refresh_token) {
            refreshToken({
                forceOauthPrefix,
                refreshToken: token.refresh_token,
            });
        } else if (accessTokenInvalid(error)) {
            clearSession();
            navigate(LOGIN_ROUTE);
        }
    }, [error]);

    const notLoginRoute =
        location.pathname !== LOGIN_ROUTE &&
        location.pathname !== LOGIN_ERROR_ROUTE &&
        location.pathname !== LOGOUT_SUCCESS_ROUTE &&
        location.pathname !== LOGIN_SILENT;

    if ((noAccessToken || refreshTokenResult?.isError) && notLoginRoute) {
        // Redirect them to the /login page, but save the current location they were
        // trying to go to when they were redirected.
        clearSession();

        const regex = 'login';

        if (errorCode) {
            searchParams.delete('error');
            searchParams.delete('error_description');
            const searchString = searchParams.toString();
            const deepLink = `${location.pathname}${
                searchString.length > 0 ? `?${searchString}` : ''
            }`;
            if (!regex.match(location.pathname)) {
                setDeeplink(deepLink);
            }
        } else if (!regex.match(location.pathname)) {
            setDeeplink(`${location.pathname}${location.search}`);
        }

        return <Navigate to={LOGIN_ROUTE} state={{ from: location }} replace />;
    }

    return (
        <BentleyHiddenIframe additionalScopes={additionalScopes}>{children}</BentleyHiddenIframe>
    );
}
